import React, {
    useCallback, useContext, useEffect, useMemo, useState
} from 'react';
import _ from 'lodash';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { Link, ModalNextProvider } from '@jutro/components';
import ModalCtrl from 'sg-modal-ctrl';
import SagaCommonMessages from 'sg-common-messages';
import SpmConstants from 'sg-spm-constants';
import CommonConstants from 'sg-common-constants';
import {
    useSgHandleSubQuestions,
    SgSessionStorageService,
    SgSubmissionUtil,
    SgActiveTestsContext,
    useSgExpandableLabel,
} from 'saga-modules';
import SpmUtils from 'sg-spm-utils';
import messages from './SgSpmDrivingHistory.messages';
import metadata from './SgSpmDrivingHistory.metadata.json5';

function SgSpmDrivingHistory(props) {
    const {
        id,
        modelVM,
        updateData,
        showErrors,
        isPolicyHolder,
        driverIndex,
        path,
        onValidate: onValidateParent,
        messageParams
    } = props;
    const {
        isComponentValid,
        onValidate,
    } = useValidation(id);
    const translator = useContext(TranslatorContext);
    const { activeMvtTests } = useContext(SgActiveTestsContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';
    const contactUsPhoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_MOTOR_PHONE_NUMBER1;
    const [invalidSubQuestions, updateInvalidSubQuestions] = useState(
        SgSessionStorageService.getItem(SgSessionStorageService.KEY.INVALID_SUB_QUESTIONS)
    );
    const isRetrieval = SgSessionStorageService.getItem(SgSessionStorageService.KEY.IS_RETRIEVAL_JOURNEY) || false;

    const adobeIdSuffix = isPolicyHolder ? 'PolicyHolder' : 'AdditionalDriver';

    const driverModelPath = `${path}[${driverIndex}]`;
    const driverModel = _.get(modelVM, driverModelPath);
    const driverResidencyPath = isPolicyHolder ? 'baseData.motorAccountHolder' : `${driverModelPath}`;
    const driverResidencyModel = _.get(modelVM, driverResidencyPath);
    const driverDateOfBirthPath = isPolicyHolder ? 'baseData.motorAccountHolder' : `${driverModelPath}.person`;
    const driverDOBModel = _.get(modelVM, driverDateOfBirthPath);

    const showUpdateNeeded = useMemo(() => {
        return (SgSubmissionUtil.isAggregatorQuote(modelVM) || isRetrieval === 'true')
            && isPolicyHolder === true
            && SpmUtils.hasToUpdateCarsInHousehold(modelVM);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Build the option list for residency years
    const residencyYears = useMemo(() => {
        let earliestYear = _.get(driverDOBModel, 'dateOfBirth.value.year') || 1900;
        if (typeof earliestYear === 'string') {
            earliestYear = parseInt(earliestYear, 10);
        }
        const residencyYearsList = [];
        const currentYear = new Date().getUTCFullYear();
        for (let i = earliestYear; i <= currentYear; i += 1) {
            residencyYearsList.unshift({
                displayName: i.toString(),
                id: i.toString()
            });
            if (i === currentYear) {
                residencyYearsList.unshift({
                    displayName: 'Non-resident',
                    id: 'Non-resident'
                });
                // last iteration of loop, if user has changed their dob to be after residency year
                // make sure that the ukResidencyYear previously chosen is still valid.
                if (_.find(residencyYearsList, { id: driverResidencyModel.ukResidencyYear.value }) === undefined) {
                    driverResidencyModel.ukResidencyYear.value = undefined;
                }
            }
        }
        return residencyYearsList;
    }, [driverDOBModel.dateOfBirth.value]);

    // Build the option list for Licence Types, excluding Full UK
    const licenceTypeValues = useMemo(() => {
        const licenceTypeAvailableValues = [];
        const filteredLicenceOptions = _.reject(driverModel.driverLicenseScope.aspects.availableValues,
            (option) => option.code === SpmConstants.LICENCE_SCOPE_FULL_UK);
        _.each(filteredLicenceOptions, (licenceType) => {
            licenceTypeAvailableValues.push({
                code: licenceType.code,
                name: translator({
                    id: licenceType.name,
                    defaultMessage: licenceType.name
                })
            });
        });
        return licenceTypeAvailableValues;
    }, [driverModel.fullUkLicence]);

    const licenceYearsValues = useMemo(() => {
        const licenceYearsAvailableValues = [];
        _.each(_.reverse([...driverModel.driverLicenseYears.aspects.availableValues]), (licenceYearsObject) => {
            licenceYearsAvailableValues.push({
                code: licenceYearsObject.code,
                name: translator({
                    id: licenceYearsObject.name,
                    defaultMessage: licenceYearsObject.name
                })
            });
        });
        return licenceYearsAvailableValues;
    }, [driverModel, translator]);

    const updateDriverData = useCallback(() => {
        if (isPolicyHolder) {
            driverModel.ukResidentSinceBirth = driverResidencyModel.ukResidentSinceBirth;
            driverModel.ukResidencyYear = driverResidencyModel.ukResidencyYear;
        }
        updateData(_.clone(modelVM));
    }, [isPolicyHolder, driverModel, driverResidencyModel, modelVM, updateData]);

    const onValidateDriver = useCallback((isValid) => {
        // custom validation for 'fake' model
        const isUkLicenceValid = driverModel.fullUkLicence !== undefined;
        const isUkResidencyYearValid = driverResidencyModel.ukResidentSinceBirth.value === true
            || _.get(modelVM, `${driverResidencyPath}.ukResidencyYear.value`) !== undefined;
        const isDrivingLicenceValid = _.get(modelVM, `${driverModelPath}.driverLicenseScope.value`) !== undefined;
        onValidate(isValid && isUkLicenceValid && isDrivingLicenceValid && isUkResidencyYearValid, id);
    }, [driverModel, driverResidencyModel, modelVM, driverResidencyPath, driverModelPath, onValidate, id]);

    const updateLicenceType = useCallback((value, fieldPath) => {
        _.set(modelVM, fieldPath, value);
        if (value === true) { // Select Full UK Licence type
            _.set(modelVM, `${driverModelPath}.driverLicenseScope`,
                _.find(driverModel.driverLicenseScope.aspects.availableValues, { code: SpmConstants.LICENCE_SCOPE_FULL_UK }));
        } else {
            _.set(modelVM, `${driverModelPath}.driverLicenseScope`, undefined);
        }
        updateDriverData();
    }, [driverModel, driverModelPath, modelVM, updateDriverData]);

    const updateHasAccessToOtherVehicles = useCallback((value, fieldPath) => {
        _.set(modelVM, fieldPath, value);
        if (!isPolicyHolder) {
            updateDriverData();
            return;
        }
        if (value === false) { // Set num of cars as 1 if no other cars are used
            _.set(modelVM, `${driverModelPath}.carsKeptAtHousehold`,
                _.find(driverModel.carsKeptAtHousehold.aspects.availableValues, { code: SpmConstants.DEFAULT_CARS_KEPT_AT_HOUSEHOLD_CODE }));
        } else {
            _.set(modelVM, `${driverModelPath}.carsKeptAtHousehold`, undefined);
        }
        updateDriverData();
    }, [driverModelPath, isPolicyHolder, modelVM, updateDriverData]);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    useEffect(() => {
        if (_.isNil(driverModel.fullUkLicence) && !_.isNil(driverModel.driverLicenseScope.value)) {
            driverModel.fullUkLicence = driverModel.driverLicenseScope.value.code === SpmConstants.LICENCE_SCOPE_FULL_UK;
        }
    }, [driverModel.fullUkLicence]);

    // clear values of hidden fields
    useSgHandleSubQuestions({
        updateModel: updateDriverData,
        updateInvalidSubQuestions,
        modelVM,
        paths: [
            `${driverModelPath}.driverLicenseYears`,
            {
                path: `${driverModelPath}.driverLicenseScope`,
                visible: driverModel.fullUkLicence === false
            },
            {
                path: `${driverResidencyPath}.ukResidencyYear`,
                required: driverResidencyModel.ukResidentSinceBirth.value === false
            },
            {
                path: `${driverModelPath}.carsKeptAtHousehold`,
                visible: driverModel.hasAccessToOtherVehicles.value === true,
            },
        ]
    });

    const showMoreInfoModal = useCallback((event) => {
        event.preventDefault();
        const modalProps = {
            title: translator(messages.insuranceDeclinedModalHeader),
            closeBtnLabel: translator(SagaCommonMessages.ModalMessageClose),
            className: 'sgInfoModal',
            modalJsx: <>{translator(messages.insuranceDeclinedModalInfo)}</>,
            titleVisibility: true
        };

        ModalNextProvider.showModal(
            <ModalCtrl {...modalProps} />
        );
    }, [translator]);

    const licenceHelpTextContent = useMemo(() => {
        return (
            <>
                <b>
                    {translator(messages.fullUkLicenceHelpTextBold,
                        { pronoun: isPolicyHolder ? messageParams.pronoun : translator(SagaCommonMessages.themPronoun) })}
                </b>
                <br />
                { translator(messages.fullUkLicenceHelpText, {
                    pronoun: messageParams.pronoun,
                    phoneNumber: isMobile
                        ? <a href={`tel:${contactUsPhoneNumber.replaceAll(' ', '')}`}>{contactUsPhoneNumber}</a>
                        : contactUsPhoneNumber
                }) }
            </>
        );
    }, [isMobile, translator]);

    const getInsuranceDeclinedHelpText = () => {
        return (
            <>
                {translator(messages.insuranceDeclinedHelpText, messageParams)}
                &nbsp;
                <Link href="/" id="insuranceDeclinedMoreHelpText" onClick={showMoreInfoModal}>
                    Find out more.
                </Link>
            </>
        );
    };

    const ukResidentSinceBirthHelpText = useSgExpandableLabel(
        messages.ukResidentSinceBirthHelpTextP1,
        messages.ukResidentSinceBirthHelpTextP2,
        messageParams
    );

    const fullUkLicenceSubLabel = useSgExpandableLabel(
        <b>
            {translator(messages.fullUkLicenceHelpTextBold,
                { pronoun: isPolicyHolder ? messageParams.pronoun : translator(SagaCommonMessages.themPronoun) })}
        </b>,
        <>
            <br />
            { translator(messages.fullUkLicenceHelpText, {
                pronoun: messageParams.pronoun,
                phoneNumber: contactUsPhoneNumber
                // During Rollout/Rollback of WOC-2972 we will decide which version for phoneNumber we keep
                // phoneNumber: isMobile
                //     ? <a href={`tel:${contactUsPhoneNumber.replaceAll(' ', '')}`}>{contactUsPhoneNumber}</a>
                //     : contactUsPhoneNumber
            }) }
        </>
    );

    const overrideProps = {
        '@field': {
            onValidationChange: onValidateDriver
        },
        ukResidentSinceBirth: {
            path: `${driverResidencyPath}.ukResidentSinceBirth`,
            label: translator(messages.ukResidentSinceBirthLabel, messageParams),
            secondaryLabel: activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_2972)
                ? ukResidentSinceBirthHelpText : translator(messages.ukResidentSinceBirthHelpText, messageParams),
            adobeID: `ukResidentSinceBirth${adobeIdSuffix}`
        },
        ukResidencyYear: {
            path: `${driverResidencyPath}.ukResidencyYear`,
            label: translator(messages.ukResidencyYearLabel, messageParams),
            visible: driverResidencyModel.ukResidentSinceBirth.value === false,
            required: driverResidencyModel.ukResidentSinceBirth.value === false,
            availableValues: [...residencyYears],
            showErrors: invalidSubQuestions.includes(`${driverResidencyPath}.ukResidencyYear`)
        },
        fullUkLicence: {
            path: `${driverModelPath}.fullUkLicence`,
            label: translator(messages.fullUkLicenceLabel, messageParams),
            secondaryLabel: activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_2972) ? fullUkLicenceSubLabel : licenceHelpTextContent,
            onValueChange: updateLicenceType,
            adobeID: `fullUkLicence${adobeIdSuffix}`,
            validationMessages: driverModel.fullUkLicence === undefined ? [translator(messages.fullUkLicenceValidation, messageParams)] : null
        },
        drivingLicenceTypeSelect: {
            path: `${driverModelPath}.driverLicenseScope`,
            label: translator(messages.driverLicenseScopeLabel, messageParams),
            visible: driverModel.fullUkLicence === false,
            required: driverModel.fullUkLicence === false,
            adobeID: `drivingLicenceTypeSelect${adobeIdSuffix}`,
            availableValues: [...licenceTypeValues],
            showErrors: invalidSubQuestions.includes(`${driverModelPath}.driverLicenseScope`)
        },
        drivingLicenceYearsSelect: {
            path: `${driverModelPath}.driverLicenseYears`,
            label: translator(messages.driverLicenseYearsLabel, messageParams),
            adobeID: `drivingLicenceYearsSelect${adobeIdSuffix}`,
            visible: driverModel.driverLicenseScope.value && driverModel.driverLicenseScope.value.code !== SpmConstants.LICENCE_SCOPE_NO_LICENCE,
            availableValues: licenceYearsValues,
            showErrors: invalidSubQuestions.includes(`${driverModelPath}.driverLicenseYears`)
        },
        hasAccessToOtherVehicles: {
            path: `${driverModelPath}.hasAccessToOtherVehicles`,
            label: translator(messages.hasAccessToOtherVehiclesLabel, messageParams),
            adobeID: `hasAccessToOtherVehicles${adobeIdSuffix}`,
            onValueChange: updateHasAccessToOtherVehicles,
        },
        updateNeeded: {
            visible: showUpdateNeeded
        },
        numberOfCarsInHousehold: {
            path: `${driverModelPath}.carsKeptAtHousehold`,
            label: translator(messages.numberOfCarsInHousehold),
            visible: !!isPolicyHolder && driverModel.hasAccessToOtherVehicles.value === true,
            showErrors: invalidSubQuestions.includes(`${driverModelPath}.carsKeptAtHousehold`)
        },
        insuranceDeclined: {
            path: `${driverModelPath}.isInsuranceDeclined`,
            label: translator(messages.insuranceDeclinedLabel, messageParams),
            secondaryLabel: getInsuranceDeclinedHelpText(),
            visible: isPolicyHolder === true
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            showErrors={showErrors}
            onModelChange={updateDriverData}
            onValidationChange={onValidateDriver}
            overrideProps={overrideProps}
        />
    );
}

export default SgSpmDrivingHistory;
